/**
 * GovernanceLinkSelector - Searchable hierarchical selector for linking artifacts to work
 * Modal with tree navigation, search, and multi-select
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  X, Search, ChevronRight, ChevronDown, Check, Folder, File,
  RefreshCw, AlertTriangle, GitBranch, AlertCircle, Link2,
  Building2, Briefcase, Target, Layers, FileText, ListTodo
} from 'lucide-react';
import { api } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { trackEvent, TELEMETRY_EVENTS } from '../utils/telemetry';
import { toast } from 'sonner';

const MAX_SELECTIONS = 200;

// Work type icons
const WORK_TYPE_ICONS = {
  portfolio: Building2,
  program: Briefcase,
  project: Target,
  epic: Layers,
  feature: FileText,
  story: ListTodo,
  task: File
};

// Tree node component
const TreeNode = ({ node, level = 0, selected, onToggle, onExpand, expanded, loading }) => {
  const Icon = WORK_TYPE_ICONS[node.type?.toLowerCase()] || File;
  const hasChildren = node.hasChildren || (node.children?.length > 0);
  const isSelected = selected.has(node.id);
  
  return (
    <div data-testid={`tree-node-${node.id}`}>
      <div
        className={`flex items-center gap-1 py-1.5 px-2 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors ${
          isSelected ? 'bg-purple-50 border border-purple-200' : ''
        }`}
        style={{ paddingLeft: `${level * 16 + 8}px` }}
      >
        {/* Expand/collapse */}
        {hasChildren ? (
          <button
            onClick={(e) => { e.stopPropagation(); onExpand?.(node.id); }}
            className="p-0.5 hover:bg-gray-200 rounded"
          >
            {loading ? (
              <RefreshCw className="w-3.5 h-3.5 text-gray-400 animate-spin" />
            ) : expanded ? (
              <ChevronDown className="w-3.5 h-3.5 text-gray-500" />
            ) : (
              <ChevronRight className="w-3.5 h-3.5 text-gray-500" />
            )}
          </button>
        ) : (
          <span className="w-4" />
        )}
        
        {/* Checkbox */}
        <button
          onClick={() => onToggle(node.id, node)}
          className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${
            isSelected
              ? 'bg-purple-600 border-purple-600'
              : 'border-gray-300 hover:border-purple-400'
          }`}
        >
          {isSelected && <Check className="w-3 h-3 text-white" />}
        </button>
        
        {/* Icon and label */}
        <div className="flex items-center gap-2 flex-1 min-w-0" onClick={() => onToggle(node.id, node)}>
          <Icon className="w-4 h-4 text-gray-500 flex-shrink-0" />
          <span className="text-sm text-gray-800 truncate">{node.title || node.name}</span>
          {node.type && (
            <span className="text-[10px] text-gray-400 uppercase">{node.type}</span>
          )}
        </div>
      </div>
      
      {/* Children */}
      {expanded && node.children?.map(child => (
        <TreeNode
          key={child.id}
          node={child}
          level={level + 1}
          selected={selected}
          onToggle={onToggle}
          onExpand={onExpand}
          expanded={false}
        />
      ))}
    </div>
  );
};

// Main Modal Component
const GovernanceLinkSelector = ({
  isOpen,
  onClose,
  source, // 'artifact' or 'work'
  artifactType, // When linking from artifact
  artifactId,
  artifactTitle,
  workId, // When linking from work
  workTitle,
  onLinkCreated
}) => {
  const { user } = useAuth();
  const tenantId = user?.tenant?.id || user?.tenant_id;
  
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [linking, setLinking] = useState(false);
  const [tree, setTree] = useState([]);
  const [searchResults, setSearchResults] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [selectedItems, setSelectedItems] = useState(new Map());
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  const [loadingNodes, setLoadingNodes] = useState(new Set());
  
  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 250);
    return () => clearTimeout(timer);
  }, [searchQuery]);
  
  // Track selector opened
  useEffect(() => {
    if (isOpen) {
      trackEvent(TELEMETRY_EVENTS.GOV_SELECTOR_OPENED, { source });
      loadTree();
    }
  }, [isOpen]);
  
  // Search when query changes
  useEffect(() => {
    if (debouncedQuery.trim()) {
      searchWork(debouncedQuery);
      trackEvent(TELEMETRY_EVENTS.GOV_SELECTOR_SEARCHED, { qLength: debouncedQuery.length });
    } else {
      setSearchResults(null);
    }
  }, [debouncedQuery]);
  
  const loadTree = async () => {
    setLoading(true);
    try {
      // Load top-level items (portfolios, programs, projects)
      const response = await api.getHierarchyTree?.(tenantId) || { data: [] };
      
      // Fallback: try to get projects list
      if (!response.data?.length) {
        const projectsRes = await api.getProjects?.() || { data: [] };
        const projects = projectsRes.data?.projects || projectsRes.data || [];
        setTree(projects.map(p => ({
          id: p.id || p._id,
          title: p.name || p.title,
          type: 'project',
          hasChildren: true,
          children: []
        })));
      } else {
        setTree(response.data);
      }
    } catch (err) {
      console.error('Failed to load tree:', err);
      // Set empty tree on error
      setTree([]);
    } finally {
      setLoading(false);
    }
  };
  
  const searchWork = async (query) => {
    try {
      const response = await api.searchWorkItems?.(query, tenantId) || { data: [] };
      setSearchResults(response.data?.items || response.data || []);
    } catch (err) {
      console.error('Search failed:', err);
      setSearchResults([]);
    }
  };
  
  const handleExpand = async (nodeId) => {
    if (expandedNodes.has(nodeId)) {
      const newExpanded = new Set(expandedNodes);
      newExpanded.delete(nodeId);
      setExpandedNodes(newExpanded);
      return;
    }
    
    // Load children if needed
    setLoadingNodes(prev => new Set(prev).add(nodeId));
    
    try {
      const response = await api.getWorkChildren?.(nodeId, tenantId) || { data: [] };
      const children = response.data?.children || response.data || [];
      
      // Update tree with children
      setTree(prevTree => {
        const updateNode = (nodes) => {
          return nodes.map(node => {
            if (node.id === nodeId) {
              return { ...node, children, hasChildren: children.length > 0 };
            }
            if (node.children) {
              return { ...node, children: updateNode(node.children) };
            }
            return node;
          });
        };
        return updateNode(prevTree);
      });
      
      setExpandedNodes(prev => new Set(prev).add(nodeId));
    } catch (err) {
      console.error('Failed to load children:', err);
    } finally {
      setLoadingNodes(prev => {
        const next = new Set(prev);
        next.delete(nodeId);
        return next;
      });
    }
  };
  
  const handleToggle = useCallback((nodeId, node) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(nodeId)) {
        next.delete(nodeId);
        setSelectedItems(prevItems => {
          const newItems = new Map(prevItems);
          newItems.delete(nodeId);
          return newItems;
        });
      } else {
        if (next.size >= MAX_SELECTIONS) {
          toast.error(`Maximum ${MAX_SELECTIONS} items can be selected`);
          return prev;
        }
        next.add(nodeId);
        setSelectedItems(prevItems => {
          const newItems = new Map(prevItems);
          newItems.set(nodeId, { id: nodeId, title: node.title || node.name, type: node.type });
          return newItems;
        });
      }
      return next;
    });
  }, []);
  
  const handleLink = async () => {
    if (selected.size === 0) return;
    
    setLinking(true);
    
    try {
      const workIds = Array.from(selected);
      
      // Call link API
      const response = await api.createGovernanceLinks({
        artifactType: artifactType,
        artifactId: artifactId,
        workIds
      }, tenantId, user?.id, user?.name);
      
      // Track telemetry
      trackEvent(TELEMETRY_EVENTS.GOV_SELECTOR_CONFIRMED, { count: workIds.length });
      
      // Show result
      const { linkedCount, ignored, errors } = response.data;
      
      if (linkedCount > 0) {
        toast.success(`Linked ${linkedCount} item${linkedCount > 1 ? 's' : ''}`);
      }
      
      if (ignored.length > 0) {
        toast.info(`${ignored.length} already linked`);
      }
      
      if (errors.length > 0) {
        toast.warning(`${errors.length} failed to link`);
      }
      
      onLinkCreated?.();
      onClose();
      
    } catch (err) {
      console.error('Link failed:', err);
      toast.error(err.response?.data?.detail || 'Failed to create links');
    } finally {
      setLinking(false);
    }
  };
  
  if (!isOpen) return null;
  
  const displayItems = searchResults !== null ? searchResults : tree;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" data-testid="governance-link-selector">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl mx-4 max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="px-5 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-semibold text-gray-900">Link to Work</h3>
            <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg">
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
          
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search work items by title..."
              className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:outline-none"
              autoFocus
              data-testid="link-search-input"
            />
          </div>
          
          {/* Linking context */}
          <div className="mt-2 text-xs text-gray-500">
            Linking: <span className="font-medium text-gray-700">{artifactTitle || `${artifactType} ${artifactId}`}</span>
          </div>
        </div>
        
        {/* Body - Tree or Search Results */}
        <div className="flex-1 overflow-auto p-4 min-h-[300px]">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-6 h-6 text-gray-400 animate-spin" />
            </div>
          ) : displayItems.length === 0 ? (
            <div className="text-center py-8">
              <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Search className="w-6 h-6 text-gray-400" />
              </div>
              <p className="text-sm text-gray-500">
                {searchResults !== null ? 'No results found' : 'No work items available'}
              </p>
            </div>
          ) : searchResults !== null ? (
            // Search results - flat list
            <div className="space-y-1">
              {searchResults.map(item => (
                <div
                  key={item.id}
                  onClick={() => handleToggle(item.id, item)}
                  className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer hover:bg-gray-100 ${
                    selected.has(item.id) ? 'bg-purple-50 border border-purple-200' : ''
                  }`}
                >
                  <div className={`w-4 h-4 rounded border flex items-center justify-center ${
                    selected.has(item.id) ? 'bg-purple-600 border-purple-600' : 'border-gray-300'
                  }`}>
                    {selected.has(item.id) && <Check className="w-3 h-3 text-white" />}
                  </div>
                  {React.createElement(WORK_TYPE_ICONS[item.type?.toLowerCase()] || File, {
                    className: 'w-4 h-4 text-gray-500'
                  })}
                  <span className="text-sm text-gray-800 flex-1 truncate">{item.title || item.name}</span>
                  {item.path && (
                    <span className="text-[10px] text-gray-400 truncate max-w-[150px]">{item.path}</span>
                  )}
                </div>
              ))}
            </div>
          ) : (
            // Tree view
            <div className="space-y-0.5">
              {tree.map(node => (
                <TreeNode
                  key={node.id}
                  node={node}
                  level={0}
                  selected={selected}
                  onToggle={handleToggle}
                  onExpand={handleExpand}
                  expanded={expandedNodes.has(node.id)}
                  loading={loadingNodes.has(node.id)}
                />
              ))}
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="px-5 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              {selected.size > 0 ? (
                <span>Selected: <span className="font-medium text-purple-700">{selected.size}</span> item{selected.size > 1 ? 's' : ''}</span>
              ) : (
                <span className="text-gray-400">Select items to link</span>
              )}
              {selected.size >= MAX_SELECTIONS && (
                <span className="ml-2 text-amber-600">(max reached)</span>
              )}
            </div>
            <div className="flex gap-2">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={handleLink}
                disabled={selected.size === 0 || linking}
                className="flex items-center gap-2 px-4 py-2 text-sm text-white bg-purple-600 rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="confirm-link-btn"
              >
                {linking ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Linking...
                  </>
                ) : (
                  <>
                    <Link2 className="w-4 h-4" />
                    Link {selected.size} item{selected.size !== 1 ? 's' : ''}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GovernanceLinkSelector;
